/**
 * FOLDER STRUCTURE TYPES - Tipos genéricos para estructura de carpetas
 * 
 * Este módulo define tipos reutilizables para cualquier sistema que
 * necesite organización jerárquica de carpetas.
 * 
 * Casos de uso:
 * - Notas del terapeuta (formaciones, libros)
 * - Documentos de pacientes
 * - Recursos de escuelas
 * - Biblioteca de media
 */

// ============================================
// TIPOS DE PROPIETARIO
// ============================================

export type OwnerType = 'therapist' | 'patient' | 'school' | 'admin' | 'user';

// ============================================
// MODELO DE CARPETA
// ============================================

export interface Folder {
    id: string;

    // Propietario
    ownerId: string;
    ownerType: OwnerType;

    // Información básica
    name: string;
    description?: string;
    icon?: string;           // Emoji o icon name
    color?: string;          // Hex color

    // Jerarquía
    parentId?: string;       // ID de carpeta padre (null = raíz)
    path: string[];          // IDs de ancestros para queries: ['abuelo', 'padre']
    depth: number;           // 0 = raíz, 1 = primer nivel, etc.

    // Ordenamiento
    order: number;

    // Conteos (desnormalizados para performance)
    itemsCount: number;      // Items directos en esta carpeta
    totalItemsCount: number; // Items incluyendo subcarpetas
    subfolderCount: number;  // Subcarpetas directas

    // Metadatos extensibles para casos específicos
    metadata?: Record<string, unknown>;

    // Timestamps
    createdAt: string;
    updatedAt: string;
}

// ============================================
// DTOs
// ============================================

export interface CreateFolderDTO {
    name: string;
    parentId?: string;
    description?: string;
    icon?: string;
    color?: string;
    metadata?: Record<string, unknown>;
}

export interface UpdateFolderDTO {
    name?: string;
    description?: string;
    icon?: string;
    color?: string;
    order?: number;
    metadata?: Record<string, unknown>;
}

// ============================================
// ESTRUCTURAS DE ÁRBOL
// ============================================

export interface FolderTreeNode extends Folder {
    children: FolderTreeNode[];
}

export interface FolderBreadcrumb {
    id: string;
    name: string;
    icon?: string;
}

// ============================================
// INTERFAZ PARA ITEMS ASIGNABLES A CARPETAS
// ============================================

/**
 * Cualquier entidad que pueda asignarse a una carpeta
 * debe implementar esta interfaz
 */
export interface FolderableItem {
    id: string;
    folderId?: string;
    // Path de la carpeta para queries anidadas (opcional, para optimización)
    folderPath?: string[];
}

// ============================================
// OPCIONES DE OPERACIONES
// ============================================

export interface GetFoldersOptions {
    parentId?: string | null;  // null = solo raíz, undefined = todos
    includeEmpty?: boolean;    // Incluir carpetas sin items
    sortBy?: 'name' | 'order' | 'createdAt' | 'itemsCount';
    sortDir?: 'asc' | 'desc';
}

export interface DeleteFolderOptions {
    moveItemsToFolderId?: string | null;  // Mover items a otra carpeta
    deleteItems?: boolean;                 // Eliminar items (peligroso)
    recursive?: boolean;                   // Eliminar subcarpetas
}

// ============================================
// COLECCIÓN DE FIRESTORE
// ============================================

export const FOLDERS_COLLECTION = 'folders';

// ============================================
// ICONOS PREDEFINIDOS POR CONTEXTO
// ============================================

export const FOLDER_ICONS = {
    default: '📁',
    training: '🎓',
    book: '📚',
    article: '📄',
    case: '📋',
    technique: '🔧',
    research: '🔬',
    personal: '💭',
    patient: '👤',
    course: '📖',
    media: '🖼️',
};

export const FOLDER_COLORS = [
    '#007AFF', // Blue
    '#34C759', // Green
    '#FF9500', // Orange
    '#FF3B30', // Red
    '#5856D6', // Purple
    '#AF52DE', // Violet
    '#FF2D55', // Pink
    '#00C7BE', // Teal
];
